/**
 * Diagnostic banner shown on the Bank Accounts page (dev + prod Teller
 * environments) that surfaces the current `window.location.origin` with
 * one-click copy. If Teller silently refuses to proceed past the bank
 * picker, it's almost always because the current origin isn't in the
 * Teller dashboard's "Allowed origins" list — this banner removes the
 * guesswork of knowing exactly which URL to paste.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Info, Copy, Check, ExternalLink } from "lucide-react";

export default function TellerOriginBanner() {
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem("ll:teller:origin:dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    api.get("/bank-connect/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config?.configured) return null;
  // Sandbox has its own dedicated banner showing test credentials.
  if ((config.environment || "sandbox") === "sandbox") return null;
  if (dismissed) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const copy = () => {
    try { navigator.clipboard.writeText(origin); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem("ll:teller:origin:dismissed", "1"); } catch {}
  };

  return (
    <div
      className="border-l-4 p-4 mb-6 bg-[#EFF6FF]"
      style={{ borderColor: "#2563EB" }}
      data-testid="teller-origin-banner"
    >
      <div className="flex items-start gap-3">
        <Info size={18} className="text-[#1D4ED8] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-[#1E3A8A] mb-1">
            Seeing <span className="font-mono-fin">&ldquo;We are unable to process your request&rdquo;</span>? Fix it in two steps
          </div>
          <p className="text-sm text-[#1E3A8A] mb-3 leading-relaxed">
            Teller only accepts enrollments from URLs you&apos;ve whitelisted <strong>and</strong> after your app is approved for the <strong>{config.environment}</strong> environment. If either is missing, Teller shows the red &ldquo;unable to process&rdquo; error <em>after</em> you pick a bank. Fix both:
          </p>

          <ol className="text-sm text-[#1E3A8A] mb-3 space-y-2 ml-4 list-decimal">
            <li>
              <strong>Whitelist this exact URL</strong> under Teller dashboard → your app → <strong>Settings → Allowed origins</strong>:
              <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                <button
                  type="button"
                  onClick={copy}
                  data-testid="teller-origin-copy"
                  className="font-mono-fin text-sm px-3 py-1.5 bg-white border border-[#2563EB] hover:bg-[#DBEAFE] flex items-center gap-2"
                >
                  <span className="font-semibold text-[#1E3A8A]">{origin}</span>
                  {copied ? <Check size={12} className="text-[#059669]" /> : <Copy size={12} className="opacity-60" />}
                </button>
              </div>
            </li>
            <li>
              <strong>Make sure the app is activated for <code className="px-1 bg-white border border-[#2563EB]">{config.environment}</code></strong>. In the Teller dashboard, your app shows a badge next to its name — if it still says &ldquo;Sandbox&rdquo;, click &ldquo;Request development access&rdquo; and wait for approval (usually under an hour).
            </li>
          </ol>

          <a
            href="https://teller.io/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="teller-dashboard-link"
            className="inline-flex text-xs px-3 py-1.5 bg-[#2563EB] text-white hover:bg-[#1D4ED8] items-center gap-1.5"
          >
            Open Teller dashboard <ExternalLink size={11} />
          </a>
          <p className="text-xs text-[#1E3A8A] opacity-90 mt-3">
            No code change needed — reload this page after saving the dashboard. Still stuck? Email <code className="bg-white px-1 border border-[#2563EB]">support@teller.io</code> with your app ID <code className="bg-white px-1 border border-[#2563EB]">{config.application_id}</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="teller-origin-dismiss"
          className="text-xs text-[#1D4ED8] hover:text-[#1E3A8A] underline shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
